import { useCallback, useEffect, useState } from "react";
import { Alert, ButtonGroup, Modal, ToggleButton } from "react-bootstrap";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../i18n/i18n.constants";
import { useAppDispatch, useTypedSelector } from "../../../app/store";
import { selectAlertContent, selectErrorMessage, setAlertContent, setErrorMessage } from "./authSlice";
import { routes } from "../../../app/routes/routes";
import AuthAlert from "../../../features/auth/AuthAlert";
import { useAuthLoading } from "./authLoadingContext";
import { useUserRole } from "../../../hooks/useUserRole";
import { UserRolesEnum } from "../../../app/enums/userRolesEnum";
import "./AuthLayout.scss";

type Props = {
    isShow: boolean;
};

const AuthLayout = ({ isShow }: Props) => {
    const { t } = useTranslation(namespaces.auth);
    const [show, setShow] = useState(isShow);
    const { pathname } = useLocation();
    const errorMessage = useTypedSelector(selectErrorMessage);
    const alertContent = useTypedSelector(selectAlertContent);
    const { isAuthLoading } = useAuthLoading();
    const { hasRole } = useUserRole();
    const dispatch = useAppDispatch();

    // Registration is temporarily Admin-only; drop this hasRole check (and always
    // include the signUp step) once self-registration access rules are redesigned.
    const authSteps = hasRole([UserRolesEnum.Admin])
        ? [
              { path: routes.login.path, value: 1 },
              { path: routes.signUp.path, value: 2 },
          ]
        : [{ path: routes.login.path, value: 1 }];

    const mapAuthSteps = (path: string) => {
        return authSteps.find((x) => x.path === path)?.value ?? 1;
    };

    const [authStepValue, setAuthStepValue] = useState(mapAuthSteps(pathname));
    const navigate = useNavigate();

    const handleClose = useCallback(() => {
        setShow(false);
        navigate(routes.ordering.path, { replace: true });
    }, [navigate]);

    const mapTitle = (path: string) => {
        switch (path) {
            case routes.login.path:
                return t("login.title");
            case routes.signUp.path:
                return t("signUp.title");
            default:
                break;
        }
    };

    const handleAuthStep = (value: number) => {
        setAuthStepValue(value);
        const pathForNav = authSteps.find((x) => x.value === value)?.path;
        if (pathForNav && pathname !== pathForNav) {
            navigate(pathForNav);
        }
    };

    const isLonginOrSignUp = (path: string) => {
        return path === routes.login.path || path === routes.signUp.path;
    };

    useEffect(() => {
        if ((pathname === routes.login.path || pathname === routes.signUp.path) && show === false) {
            navigate(routes.ordering.path, { replace: true });
        }
    }, [isShow, navigate, pathname, show]);

    useEffect(() => {
        return () => {
            dispatch(setErrorMessage(""));
            dispatch(setAlertContent(null));
        };
    }, [dispatch]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            centered
            fullscreen="sm-down"
            dialogClassName="auth-modal"
            contentClassName="auth-modal-content"
            aria-labelledby="auth-title"
        >
            <Modal.Header closeButton className="pb-1"></Modal.Header>
            <Modal.Body className="pt-0">
                {alertContent ? (
                    <AuthAlert />
                ) : (
                    <>
                        {authSteps.length > 1 && isLonginOrSignUp(pathname) ? (
                            <ButtonGroup className="pt-0 mb-4 mt-2 w-100">
                                {authSteps.map((item, idx) => (
                                    <ToggleButton
                                        key={idx}
                                        id={`authStep-radio-${idx}`}
                                        type="radio"
                                        variant={authStepValue === item.value ? "outline-primary" : "outline-secondary"}
                                        name="authStep"
                                        value={item.value}
                                        onChange={(e) => handleAuthStep(Number(e.currentTarget.value))}
                                        className="py-3 fw-bold"
                                        disabled={isAuthLoading}
                                    >
                                        {mapTitle(item.path)}
                                    </ToggleButton>
                                ))}
                            </ButtonGroup>
                        ) : undefined}
                        {errorMessage ? (
                            <Alert
                                variant="danger"
                                className="mt-2 text-start"
                                onClose={() => dispatch(setErrorMessage(""))}
                                dismissible
                            >
                                <p className="mb-0">{errorMessage}</p>
                            </Alert>
                        ) : undefined}
                        <Outlet />
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AuthLayout;
